import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const AuditList = ({ rows }) => (
  <section className="tc-card p-5 space-y-2" data-testid="comp-audit">
    <h2 className="font-heading font-bold">Grant / revoke log</h2>
    {rows.length === 0 ? <p className="text-sm text-tc-muted" data-testid="comp-audit-empty">No comp changes yet.</p> : (
      <ul className="divide-y divide-white/5 text-sm">
        {rows.map((r, i) => (
          <li key={i} className="py-2 flex flex-wrap justify-between gap-2" data-testid="comp-audit-row">
            <span><span className={r.action === "grant" ? "text-tc-bull" : "text-tc-bear"}>{r.action}</span> {r.tier} · {r.target_email}</span>
            <span className="text-xs text-tc-muted font-mono">by {r.by_email} · {new Date(r.at).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default function AdminComp() {
  const [email, setEmail] = useState("");
  const [acct, setAcct] = useState(null);
  const [audit, setAudit] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadAudit = useCallback(() => api.get("/admin/comp/audit").then((r) => setAudit(r.data))
    .catch((e) => { if (e?.response?.status === 403) setDenied(true); else toast.error(apiErr(e)); }), []);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const find = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { setAcct((await api.get("/admin/comp/lookup", { params: { email } })).data); }
    catch (err) { setAcct(null); toast.error(apiErr(err)); }
    setBusy(false);
  };
  const act = async (kind) => {
    setBusy(true);
    try {
      const r = (await api.post(`/admin/comp/${kind}`, { email: acct.user.email, tier: "FAMILY" })).data;
      setAcct(r); toast.success(r.changed ? (kind === "grant" ? "Family comp granted." : "Comp revoked.") : "No change needed.");
      loadAudit();
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };

  if (denied) return <div className="p-8 text-tc-sub" data-testid="admin-comp-denied">Not available.</div>;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-5" data-testid="admin-comp-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Admin</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-1">Plan comps</h1>
        <p className="text-sm text-tc-sub mt-1">Give an account the Family plan at no charge, or take it back. Comps never create a Stripe subscription and are not counted as paying customers.</p>
      </div>
      <form onSubmit={find} className="tc-card p-5 flex flex-wrap gap-2" data-testid="comp-lookup-form">
        <input data-testid="comp-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="account email"
          className="flex-1 min-w-[200px] rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        <button data-testid="comp-lookup-btn" disabled={busy} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm flex items-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find account
        </button>
      </form>
      {acct && (
        <section className="tc-card p-5 space-y-3" data-testid="comp-account">
          <div className="text-sm"><span className="font-semibold">{acct.user.name}</span> · {acct.user.email} · {acct.user.linked_members} linked</div>
          <div className="text-sm" data-testid="comp-account-tier">Plan now: <span className="font-semibold">{acct.tier}</span>
            {acct.paid_tier !== "FREE" && <span className="text-tc-muted"> (paid {acct.paid_tier} subscription takes precedence)</span>}</div>
          <div className="text-sm text-tc-sub" data-testid="comp-account-status">{acct.comp ? `Family comp active since ${new Date(acct.comp.granted_at).toLocaleDateString()}` : "No comp on this account."}</div>
          <div className="flex gap-2">
            {!acct.comp
              ? <button data-testid="comp-grant-btn" disabled={busy} onClick={() => act("grant")} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm">Grant Family comp</button>
              : <button data-testid="comp-revoke-btn" disabled={busy} onClick={() => act("revoke")} className="rounded-lg border border-tc-bear/50 text-tc-bear px-4 py-2 text-sm">Revoke comp</button>}
          </div>
        </section>
      )}
      {audit && <AuditList rows={audit.entries} />}
    </div>
  );
}
